import type { Rng } from '../../core/rng/Rng';
import { SIM_HZ } from '../../core/GameLoop';
import { ELITE_AFFIXES, spawnDensityMult, type EliteAffix, type EnemyType } from '../entities/enemyDefs';
import type { Room } from '../../map/MapGen';
import { rollArtifactDrop, rollArtifactProc } from '../artifacts/ArtifactSystem';
import type { ArtifactProc } from '../artifacts/ArtifactDefs';
import { rollQuality } from '../meta/economy';
import { tierEliteDropChance, tierQualityFloor } from '../run/tiers';

/**
 * Deterministic spawn schedule (seeded `spawns` stream, built at floor start)
 * + reactive placement (exact tile chosen at spawn time, biased off-screen —
 * gameplay rng, per the determinism split).
 */

export interface SpawnEvent {
  atStep: number;
  type: EnemyType;
  count: number;
  regionIndex: number;
  /** Deterministic ordinal — keys the per-emitter pattern rng stream. */
  ordinal: number;
  elite: boolean;
  affix: EliteAffix | null;
  /** Pre-rolled at floor start from the loot stream — kill order can't perturb. */
  dropArtifactId: string | null;
  /** Quality roll [0.80, 1.20], also pre-rolled from the loot stream. */
  dropQuality: number;
  /** Proc roll (blessed/cursed), also pre-rolled from the loot stream. */
  dropProc: ArtifactProc;
  dropCurseId: string | null;
}

export const LIVE_CAP = 120;
/**
 * Hard concurrent-enemy ceiling for cap-bypassing spawners (splitter mites,
 * enrage): LIVE_CAP plus headroom for the ambush trio and stragglers. The
 * test ceiling sits a little above this (LIVE_CAP + 12).
 */
export const HARD_ENEMY_CAP = LIVE_CAP + 6;

/**
 * Finite per-floor population (doc §1): the floor's schedule delivers exactly
 * this many regulars, then goes quiet — the thinning tail pushes campers
 * deeper. Elites are the bonus population on top (fixed count, so the
 * loot-stream draw count per floor is a function of floor+tier only).
 * Outside the budget: splitter mites, ambush elites, enrage spawns.
 *
 * Fillability invariants (pinned in spawnBudget.test.ts):
 *  I1 — floors 1–2 cannot fill the kill bar even at 100% clear.
 *  I2 — every floor ≥ 3 fills it from regulars alone with ≥30% headroom.
 */
export function spawnBudget(floor: number): number {
  return 520 + 120 * (floor - 1);
}

export function eliteBudget(floor: number): number {
  return Math.min(18, 10 + 2 * (floor - 1));
}

// Melee-dominated mix (Survivors-style): watcher is the lone ranged type.
// Mites are splitter-spawned only — never in the bag.
const WAVE_TYPES: readonly EnemyType[] = [
  'shambler', 'shambler', 'shambler', 'shambler',
  'shambler', 'shambler', 'shambler', 'shambler',  // 40%
  'husk', 'husk',                                  // 10%
  'chanter', 'chanter',                            // 10%
  'watcher',                                       //  5% — lone ranged type; kept scarce
  'splitter', 'splitter',                          // 10%
  'flanker', 'flanker',                            // 10%
  'leech', 'leech',                                // 10%
  'brute',                                         //  5% — heavy; rare by design
];

export function buildSchedule(
  rng: Rng,
  floor: number,
  regionCount: number,
  lootRng: Rng,
  droppedArtifacts: Set<string>,
  tier = 0,
): SpawnEvent[] {
  const events: SpawnEvent[] = [];
  const density = spawnDensityMult(floor);
  // Tier moves gate THRESHOLDS and roll FLOORS only — never draw counts.
  const dropChance = tierEliteDropChance(tier);
  const qualityFloor = tierQualityFloor(tier);
  const budget = spawnBudget(floor);
  const elites = eliteBudget(floor);
  let spawned = 0;
  let eliteCount = 0;
  let t = 2.0; // grace period at floor entry
  let ordinal = 0;
  let nextElite = rng.range(40, 70);

  const pushElite = (atSeconds: number): void => {
    // Affix from the spawns stream (part of the elite schedule); the drop,
    // its quality roll, AND its proc/curse roll come from the loot stream,
    // consumed here IN ORDER at floor start (quality/proc drawn only when a
    // drop exists — drop presence is itself deterministic, so draw order
    // stays fixed).
    const drops = lootRng.next() < dropChance
      ? rollArtifactDrop(lootRng, droppedArtifacts)
      : null;
    const quality = drops !== null ? rollQuality(lootRng, qualityFloor) : 0;
    const procRoll = drops !== null
      ? rollArtifactProc(lootRng)
      : { proc: 'none' as const, curseId: null };
    events.push({
      atStep: Math.round(atSeconds * SIM_HZ),
      // Elite-eligible pool: splitter excluded (dressing doesn't propagate
      // to mites), leech excluded (a durable healer deletes its identity).
      type: rng.pick(['watcher', 'husk', 'chanter', 'brute', 'flanker'] as const),
      count: 1,
      regionIndex: rng.int(Math.max(1, regionCount)),
      ordinal: ordinal++,
      elite: true,
      affix: rng.pick(ELITE_AFFIXES),
      dropArtifactId: drops,
      dropQuality: quality,
      dropProc: procRoll.proc,
      dropCurseId: procRoll.curseId,
    });
    eliteCount++;
  };

  // 30-min ceiling is a degenerate-tuning backstop only — the budget is the
  // real loop bound.
  while (spawned < budget && t < 30 * 60) {
    let interval = rng.range(2.4, 4.2) / density;
    // Thinning tail: past 75% of the budget the floor audibly dies — waves
    // stretch ×1→×4. Deterministic (schedule-time), no extra draws.
    const progress = spawned / budget;
    if (progress > 0.75) interval *= 1 + 3 * ((progress - 0.75) / 0.25);
    t += interval;
    const count = Math.min(
      12,
      2 + rng.int(4) + Math.floor((floor - 1) * 1.2),
      budget - spawned, // clamp the last wave: land exactly on budget
    );
    spawned += count;
    events.push({
      atStep: Math.round(t * SIM_HZ),
      type: rng.pick(WAVE_TYPES),
      count,
      regionIndex: rng.int(Math.max(1, regionCount)),
      ordinal: ordinal++,
      elite: false,
      affix: null,
      dropArtifactId: null,
      dropQuality: 0,
      dropProc: 'none',
      dropCurseId: null,
    });
    if (t > nextElite && eliteCount < elites) {
      pushElite(t + 1);
      nextElite = t + rng.range(45, 75);
    }
  }
  // The elite count is FIXED per floor — schedule any not yet placed into the
  // tail so the loot-stream draw count stays a function of (floor, tier).
  while (eliteCount < elites) {
    t += rng.range(45, 75);
    pushElite(t);
  }
  return events;
}

/**
 * Runtime cursor over the schedule. Deferred events (live cap) fire as slots
 * free up. Placement callback picks the actual tile reactively.
 */
export class SpawnDirector {
  private step = 0;
  private cursor = 0;
  private deferred: SpawnEvent[] = [];

  constructor(
    private readonly schedule: SpawnEvent[],
    readonly regions: readonly Room[],
  ) {}

  /** The floor's finite population is fully delivered — it only thins now. */
  get exhausted(): boolean {
    return this.cursor >= this.schedule.length && this.deferred.length === 0;
  }

  tick(liveEnemies: number, spawn: (ev: SpawnEvent) => void): void {
    this.step++;
    while (this.deferred.length > 0 && liveEnemies < LIVE_CAP) {
      spawn(this.deferred.shift()!);
      liveEnemies++;
    }
    while (this.cursor < this.schedule.length && this.schedule[this.cursor]!.atStep <= this.step) {
      const ev = this.schedule[this.cursor++]!;
      if (liveEnemies + ev.count > LIVE_CAP) {
        this.deferred.push(ev);
      } else {
        spawn(ev);
        liveEnemies += ev.count;
      }
    }
  }
}
